import React, { useState, useEffect, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from './AddCard.module.css';
import { useModal } from '../../../../helpers/hooks/modalContext';
import {
  createPaymentOption,
  getUserByToken,
  authPaymentToken,
  authPayment,
  saveUserIp,
  getBuildingDetailsById,
  getBuildingDetails,
} from '../../../../auth/_requests';
import { useAuth } from '../../../../auth/Auth';
import { PaymentMethodType, SafeCharge, buildingDetails } from '../../../../helpers/utils/types';
import {
  SAFECHARGE_FIELD_BASE_STYLE,
  SAFECHARGE_FIELDS_OPTIONS,
} from '../../../../constants/fontConstants';
import { motion } from 'framer-motion';
import { FetchAddress } from '../../../../helpers/utils/utils';
import { AddCardFormValues } from '../../../../helpers/DatePicker/DatePicker';
import { ChevronLeft, Pencil, X } from 'lucide-react';
import { showToast } from '../../../../helpers/utils/showToast';
import { usePaymentOptions } from '../../../hooks/PaymentOptionsContext';
import { logThirdPartyError } from '../../../utils/safeSentryError';
import { trackPaymentMethodAdded } from '../../../utils/analytics';
import {
  getNuveiRedirectUrls,
  normalizeNuveiTransactionStatus,
  saveNuveiPaymentContext,
  clearNuveiPaymentContext,
  getNuveiUserFacingErrorMessage,
  getNuveiRawErrorDescription,
  buildAddCardBillingAddress,
  createNuvei3dsEventHandlers,
  isNuveiPayment3ds,
  getNuvei3dsFlow,
} from '../../../utils/nuveiPaymentUtils';
import { hasUsableBuildingAddress } from '../../../utils/payFlowGuard';

interface SafeChargeProps {
  safeCharge: SafeCharge;
}

// Canadian postal code: A1A 1A1 (space/hyphen optional).
const CA_POSTAL_CODE_REGEX = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
// US ZIP code: 5 digits, optionally followed by a 4-digit extension.
const US_ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;
// Fallback for any other country: alphanumeric, spaces and hyphens, 3-10 chars.
const GENERIC_POSTAL_CODE_REGEX = /^[A-Za-z0-9 -]{3,10}$/;

const errorMessages: Record<string, string> = {
  incomplete_card_number: 'Card number is incomplete',
  incorrect_card_number: 'The card number is incorrect',
  incomplete_cvc: 'CVC is incomplete',
  invalid_cvc: 'The CVC is invalid',
  incomplete_date: 'Expiration date is incomplete',
  date_in_past: 'Expiration date is in the past',
  invalid_expiration_year: 'Expiration year is invalid',
};

const AddCard: React.FC<SafeChargeProps> = memo(({ safeCharge }) => {
  const navigate = useNavigate();
  const { setModal, modalState } = useModal();
  const payRentSetupFlow = modalState?.payRentSetupFlow === true;
  const { auth, currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [sessionToken, setSessionToken] = useState<string>('');
  const [cardErrors, setCardErrors] = useState<any>({});
  const [isSaveManually, setIsManually] = useState(false);
  const cardNumberRef = useRef<any>(null);
  const cardExpiryRef = useRef<any>(null);
  const cardCvcRef = useRef<any>(null);
  const addressInputRef = useRef(null);
  const [isAutoCompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [sameAsPropertyAddress, setSameAsPropertyAddress] = useState(false);
  const [propertyAddress, setPropertyAddress] = useState<buildingDetails['address'] | null>(null);
  const { refreshPaymentOptions } = usePaymentOptions();

  useEffect(() => {
    const fetchSessionToken = async () => {
      try {
        const redirectUrls = getNuveiRedirectUrls();
        const response = await authPaymentToken(
          auth?.data.token,
          PaymentMethodType.CreditDebitCard,
          redirectUrls
        );
        setSessionToken(response.sessionToken);
      } catch (error) {
        console.error('Error fetching session token:', error);
        logThirdPartyError(new Error('Add Card - failed to fetch Nuvei session token'), {
          flow: 'add_card',
          step: 'auth_payment_token',
          paymentMethodType: PaymentMethodType.CreditDebitCard,
          error,
        });
      }
    };
    fetchSessionToken();
  }, [auth?.data.token]);

  useEffect(() => {
    const fetchPropertyAddress = async () => {
      if (!auth?.data.token) return;

      try {
        const tenantBuildingId = modalState?.tenantBuildingId;
        const response = tenantBuildingId
          ? await getBuildingDetailsById(auth.data.token, tenantBuildingId)
          : await getBuildingDetails(auth.data.token);

        const buildings = response?.data ?? [];
        const matchedBuilding = tenantBuildingId
          ? buildings.find((b) => b.building.tenantBuildingId === tenantBuildingId) ??
            buildings[0]
          : buildings[0];

        if (hasUsableBuildingAddress(matchedBuilding?.address)) {
          setPropertyAddress(matchedBuilding.address);
        }
      } catch (error) {
        console.error('Error fetching property address:', error);
      }
    };

    fetchPropertyAddress();
  }, [auth?.data.token, modalState?.tenantBuildingId]);

  useEffect(() => {
    const handleFieldChange = (event: any) => {
      const { field, error } = event;

      if (error) {
        const friendlyMessage = errorMessages[error.id] || 'An error occurred';
        setCardErrors((prevErrors: any) => ({
          ...prevErrors,
          [field]: friendlyMessage,
        }));
      } else {
        setCardErrors((prevErrors: any) => ({
          ...prevErrors,
          [field]: '',
        }));
      }
    };

    if (safeCharge) {
      const safeChargeFields = safeCharge.fields({
        ...SAFECHARGE_FIELDS_OPTIONS,
      });

      if (!cardNumberRef.current) {
        cardNumberRef.current = safeChargeFields.create('ccNumber', {
          style: { base: SAFECHARGE_FIELD_BASE_STYLE },
        });
        cardNumberRef.current.attach('#card-number');
        cardNumberRef.current.on('change', handleFieldChange);
      }
      if (!cardExpiryRef.current) {
        cardExpiryRef.current = safeChargeFields.create('ccExpiration', {
          style: { base: SAFECHARGE_FIELD_BASE_STYLE },
        });
        cardExpiryRef.current.attach('#card-expiry');
        cardExpiryRef.current.on('change', handleFieldChange);
      }
      if (!cardCvcRef.current) {
        cardCvcRef.current = safeChargeFields.create('ccCvc', {
          style: { base: SAFECHARGE_FIELD_BASE_STYLE },
        });
        cardCvcRef.current.attach('#card-cvc');
        cardCvcRef.current.on('change', handleFieldChange);
      }
    }
  }, [safeCharge]);

  const getUserEmail = async (): Promise<string> => {
    // First try to get email from currentUser
    if (currentUser?.user?.email) {
      return currentUser.user.email;
    }

    // If not available, fetch from API
    if (auth?.data.token) {
      try {
        const { data } = await getUserByToken(auth.data.token);
        if (data?.user?.email) {
          return data.user.email;
        }
      } catch (error) {
        console.error('Error fetching user email:', error);
      }
    }

    // If still not available, throw error
    throw new Error('Email is required but could not be retrieved');
  };

  const formik = useFormik<AddCardFormValues>({
    initialValues: {
      cardHolderName: '',
      email: '', // Not used, but required for type compatibility
      address: '',
      unitNumber: '',
      street: '',
      house: '',
      postal: '',
      city: '',
      province: '',
      provinceCode: '',
      country: 'CA',
    },
    validationSchema: Yup.object({
      cardHolderName: Yup.string().trim().required('Cardholder name is required'),
      address: Yup.string().trim().required('Address is required'),
      postal: Yup.string()
        .trim()
        .required('Postal code is required')
        .when('country', {
          is: (country: string) => (country || '').toUpperCase() === 'CA',
          then: (schema) =>
            schema.matches(
              CA_POSTAL_CODE_REGEX,
              'Enter a valid Canadian postal code (e.g. A1A 1A1)'
            ),
        })
        .when('country', {
          is: (country: string) => (country || '').toUpperCase() === 'US',
          then: (schema) =>
            schema.matches(US_ZIP_CODE_REGEX, 'Enter a valid US ZIP code (e.g. 12345 or 12345-6789)'),
        })
        .when('country', {
          is: (country: string) => !['CA', 'US'].includes((country || '').toUpperCase()),
          then: (schema) =>
            schema.matches(GENERIC_POSTAL_CODE_REGEX, 'Enter a valid postal code'),
        }),
      house: Yup.string().optional(),
      street: Yup.string().optional(),
      city: Yup.string().trim().required('City is required'),
      province: Yup.string().optional(),
      provinceCode: Yup.string()
        .trim()
        .required('Province/State code is required')
        .matches(/^[A-Za-z]{2}$/, 'Enter a 2-letter province/state code'),
      country: Yup.string().trim().required('Country is required'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setSubmitError('');

      const paymentOptionId = localStorage.getItem('selectedPaymentOption');
      if (paymentOptionId) {
        localStorage.removeItem('selectedPaymentOption');
      }

      if (!safeCharge || !sessionToken) {
        setLoading(false);
        const message = 'Payment form is not ready. Please close and try again.';
        setSubmitError(message);
        console.error('SafeCharge or session token not initialized');
        logThirdPartyError(new Error('Add Card - payment form not ready on submit'), {
          flow: 'add_card',
          step: 'submit',
          safeChargeLoaded: Boolean(safeCharge),
          hasSessionToken: Boolean(sessionToken),
        });
        return;
      }

      try {
        const userEmail = await getUserEmail();
        const billingAddress = buildAddCardBillingAddress(values, userEmail);

        const handleAddCardUpo = (
          verificationTransactionId: string,
          activeSessionToken: string,
          is3DS: boolean
        ): void => {
        const addCardUpoRequest = {
          sessionToken: activeSessionToken,
          billingAddress,
        };

        safeCharge.addCardUpo(
          {
            sessionToken: activeSessionToken,
            useFields: true,
            cardHolderName: values.cardHolderName,
            billingAddress,
          },
          async (addCardResult) => {
            const addCardUpoResponse = addCardResult;
            if (addCardResult.result === 'ADDED') {
              try {
                const paymentOption = await createPaymentOption(auth?.data.token, {
                  is3DS,
                  userPaymentOptionId: String(addCardResult.userPaymentOptionId),
                  citTransactionId:
                    verificationTransactionId ||
                    (addCardResult.citTransactionId != null
                      ? String(addCardResult.citTransactionId)
                      : ''),
                });

                if (!is3DS) {
                  try {
                    await authPayment(auth?.data.token, String(paymentOption?.id));
                  } catch (authError) {
                    console.error('Payment method verification (auth) failed:', authError);
                    logThirdPartyError(
                      new Error('Add Card - payment method verification (auth) failed'),
                      {
                        flow: 'add_card',
                        step: 'auth_payment',
                        paymentOptionId: paymentOption?.id,
                        error: authError,
                      }
                    );
                  }
                }

                await refreshPaymentOptions();

                if (paymentOption?.id) {
                  localStorage.setItem('selectedPaymentOption', paymentOption.id);
                }

                showToast({
                  message: 'Card added successfully',
                  type: 'success',
                });

                trackPaymentMethodAdded('card');

                const tenantBuildingId = modalState?.tenantBuildingId;
                const payPlan = modalState?.feePaymentPlan === 'direct' ? 'direct' : 'autopay';

                setLoading(false);
                clearNuveiPaymentContext();

                if (tenantBuildingId) {
                  setModal(null);
                  navigate('/pay-rent', {
                    state: {
                      buildingId: tenantBuildingId,
                      initialStep: is3DS ? 2 : 1,
                      payPlan,
                      syncPayFlowFromState: true,
                    },
                  });
                } else {
                  setModal(null);
                }
              } catch (error: any) {
                const apiMessage =
                  typeof error === 'string' ? error : String(error?.message ?? '');
                let errorMessage = 'Failed to create payment option.';
                if (apiMessage.includes('user_payment_option_exists')) {
                  errorMessage = 'User payment option already exists';
                } else if (apiMessage.includes('cit_transaction_id_required')) {
                  errorMessage =
                    'Card verification completed but could not be saved. Please try again.';
                } else if (apiMessage.includes('invalid_user_payment_option_id')) {
                  errorMessage =
                    'Card was added but could not be linked yet. Please try again in a moment.';
                }
                logThirdPartyError(new Error('Failed to create payment option after addCardUpo'), {
                  request: addCardUpoRequest,
                  response: addCardUpoResponse,
                  error,
                });

                showToast({
                  message: errorMessage,
                  type: 'error',
                });
                setSubmitError(errorMessage);
                setLoading(false);
                clearNuveiPaymentContext();
              }
            } else {
              const errorMessage = getNuveiUserFacingErrorMessage(addCardResult, 'add-card');
              const rawError = getNuveiRawErrorDescription(addCardResult);
              console.error('Error adding card:', errorMessage);
              logThirdPartyError(new Error('Error adding Card - addCardUpo failed'), {
                request: addCardUpoRequest,
                response: addCardUpoResponse,
                error: rawError || errorMessage,
              });
              showToast({
                message: errorMessage,
                type: 'error',
              });
              setSubmitError(errorMessage);
              setLoading(false);
              clearNuveiPaymentContext();
            }
          }
        );
      };

      const handleVerificationFailure = (
        result: Record<string, unknown>,
        request: Record<string, unknown>
      ): void => {
        const errorMessage = getNuveiUserFacingErrorMessage(result, 'add-card');
        const rawError = getNuveiRawErrorDescription(result);
        console.error('Error adding card:', errorMessage);
        logThirdPartyError(new Error('Error adding Card - createPayment failed'), {
          request,
          response: result,
          error: rawError || errorMessage,
        });
        showToast({
          message: errorMessage,
          type: 'error',
        });
        setSubmitError(errorMessage);
        setLoading(false);
        clearNuveiPaymentContext();
      };

        const redirectUrls = getNuveiRedirectUrls();
        const tokenResponse = await authPaymentToken(
          auth?.data.token,
          PaymentMethodType.CreditDebitCard,
          redirectUrls
        );
        const activeSessionToken = tokenResponse.sessionToken;
        setSessionToken(activeSessionToken);

        saveNuveiPaymentContext({
          flow: 'add-card',
          tenantBuildingId: modalState?.tenantBuildingId,
          payPlan: modalState?.feePaymentPlan === 'direct' ? 'direct' : 'autopay',
        });

        const ipAddress = await saveUserIp(auth?.data.token);
        const nuvei3dsEvents = createNuvei3dsEventHandlers();
        const createPaymentRequest = {
          sessionToken: activeSessionToken,
          userTokenId: currentUser?.user?.userId,
          cardHolderName: values.cardHolderName,
          paymentOption: cardNumberRef.current,
          billingAddress,
          forceWebChallenge: true,
          ...(ipAddress ? { deviceDetails: { ipAddress } } : {}),
          onPaymentEvent: nuvei3dsEvents.onPaymentEvent,
        };

        console.log('[AddCard] Trying createPayment for card verification');
        safeCharge.createPayment(createPaymentRequest, (result) => {
          console.log('[AddCard] createPayment response', result);
          nuvei3dsEvents.clearNuvei3dsChallengeState();
          const outcome = normalizeNuveiTransactionStatus(result);

          if (outcome === 'pending') {
            setSubmitError('Complete verification with your bank if prompted.');
            return;
          }

          if (outcome === 'cancelled') {
            setLoading(false);
            clearNuveiPaymentContext();
            const message = 'Card verification was cancelled.';
            setSubmitError(message);
            showToast({ message, type: 'error' });
            return;
          }

          if (outcome === 'approved') {
            const sawAcsChallenge = nuvei3dsEvents.wasChallengePresented();
            const is3DS = isNuveiPayment3ds(result, sawAcsChallenge);
            console.log('[AddCard] createPayment approved, proceeding with addCardUpo', {
              transactionId: result.transactionId ?? result.transactionID,
              is3DS,
              flow: getNuvei3dsFlow(result),
              sawAcsChallenge,
            });
            const createPaymentTransactionId = String(
              result.transactionId ?? result.transactionID ?? ''
            );
            handleAddCardUpo(createPaymentTransactionId, activeSessionToken, is3DS);
            return;
          }

          handleVerificationFailure(result, createPaymentRequest);
        });
      } catch (error) {
        setLoading(false);
        clearNuveiPaymentContext();
        document.body.removeAttribute('data-nuvei-3ds-challenge');
        logThirdPartyError(new Error('Error in Nuvei createPayment method - Add Card'), {
          request: null,
          response: null,
          error,
        });
        console.error('Error during createPayment or addCardUpo:', error);
        if (error instanceof Error && error.message.includes('Email is required')) {
          const message = 'Email is required but could not be retrieved. Please try again.';
          showToast({ message, type: 'error' });
          setSubmitError(message);
        }
      }
    },
  });

  useEffect(() => {
    if (formik.submitCount === 0) {
      return;
    }
    if (formik.errors.city || formik.errors.postal || formik.errors.provinceCode) {
      setIsManually(true);
    }
  }, [
    formik.submitCount,
    formik.errors.city,
    formik.errors.postal,
    formik.errors.provinceCode,
  ]);

  const applyPropertyAddressToForm = (address: buildingDetails['address']) => {
    formik.setValues({
      ...formik.values,
      address: address.fullAddress || '',
      unitNumber: address.unitNumber || '',
      house: address.buildingNumber || '',
      street: address.streetName || '',
      city: address.city || '',
      province: address.province || '',
      provinceCode: address.provinceCode || '',
      postal: address.postalCode || '',
      country: address.countryCode || 'CA',
    });
    setIsManually(true);
  };

  const handleSameAsPropertyAddressChange = (checked: boolean) => {
    setSameAsPropertyAddress(checked);

    if (checked && propertyAddress) {
      applyPropertyAddressToForm(propertyAddress);
      return;
    }

    formik.setValues({
      ...formik.values,
      address: '',
      unitNumber: '',
      house: '',
      street: '',
      city: '',
      province: '',
      provinceCode: '',
      postal: '',
      country: 'CA',
    });
    setIsManually(false);
  };

  const handleBillingFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (sameAsPropertyAddress) {
      setSameAsPropertyAddress(false);
    }
    formik.handleChange(event);
  };

  return (
    <>
      <motion.main
        className={styles.main}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <FetchAddress
          addressInputRef={addressInputRef}
          formik={formik}
          isSaveManually={setIsManually}
          setIsAutocompleteOpen={setIsAutocompleteOpen}
        />
        <div className={styles.headingOuter}>
          <div className='flex items-center gap-[12px]'>
            <button
              onClick={() =>
                setModal('add-payment-method', {
                  tenantBuildingId: modalState?.tenantBuildingId,
                  payRentSetupFlow: modalState?.payRentSetupFlow,
                  feePaymentPlan: modalState?.feePaymentPlan,
                })
              }
            >
              {' '}
              <ChevronLeft stroke='#0E0E0B' />
            </button>
            <span>{modalState?.paymentType}</span>
          </div>
          {!payRentSetupFlow && (
            <X
              className={styles.crossIcon}
              size={24}
              onClick={() => setModal(null)}
              stroke='#0E0E0B'
            />
          )}
        </div>

        <form
          onSubmit={formik.handleSubmit}
          className={styles.formOuter}
          data-clarity-mask='true'
        >
          <div
            className={`${styles.formScroll} ${isSaveManually && styles.formScrollExpanded} ${
              isAutoCompleteOpen && formik.values.address !== '' && styles.mainHidden
            }`}
          >
          {/* Cardholder Name */}
          <div className={`${styles.item} inputWrapper`}>
            <label htmlFor='card-number' className='label'>
              Card number <span className={styles.required}>*</span>
            </label>
            <div id='card-number' className='input' />
            {cardErrors.ccNumber && <div className={styles.error}>{cardErrors.ccNumber}</div>}
          </div>
          <div className={styles.formGridSingle}>
            <div
              className={`${styles.item} inputWrapper cardField`}
              title='Enter the full name of the cardholder'
            >
              <label htmlFor='cardHolderName' className='label'>
                Cardholder name <span className={styles.required}>*</span>
              </label>
              <input
                id='cardHolderName'
                type='text'
                placeholder='Cardholder name'
                className='inputField'
                {...formik.getFieldProps('cardHolderName')}
              />
              {formik.touched.cardHolderName && formik.errors.cardHolderName && (
                <div className={styles.error}>{formik.errors.cardHolderName}</div>
              )}
            </div>
          </div>

          <div className={styles.formGrid}>
            {/* Expiration Date */}
            <div className={`${styles.item} inputWrapper`}>
              <label htmlFor='card-expiry' className='label'>
                Expiration date <span className={styles.required}>*</span>
              </label>
              <div id='card-expiry' className='input' />
              {cardErrors.ccExpiration && (
                <div className={styles.error}>{cardErrors.ccExpiration}</div>
              )}
            </div>

            {/* CVV */}
            <div className={`${styles.item} inputWrapper`}>
              <label htmlFor='card-cvc' className='label'>
                CVV <span className={styles.required}>*</span>
              </label>
              <div id='card-cvc' className='input' />
              {cardErrors.ccCvc && <div className={styles.error}>{cardErrors.ccCvc}</div>}
            </div>
          </div>

          {/* Address */}
          <div className={styles.formGridSingle}>
            <div
              className={`${styles.item} inputWrapper`}
              title='Enter the billing address associated with your card'
            >
              <div className={`${styles.addressOuter} `}>
                <label htmlFor='address' className='label'>
                  Billing address <span className={styles.required}>*</span>
                </label>
                <button
                  className={styles.editOuter}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsManually(!isSaveManually);
                  }}
                >
                  <Pencil width={20} height={20} />
                  <span>Enter manually</span>
                </button>
              </div>
              {propertyAddress && (
                <label htmlFor='sameAsPropertyAddress' className={styles.sameAsProperty}>
                  <input
                    id='sameAsPropertyAddress'
                    type='checkbox'
                    checked={sameAsPropertyAddress}
                    onChange={(e) => handleSameAsPropertyAddressChange(e.target.checked)}
                    className={styles.sameAsPropertyCheckbox}
                  />
                  <span>Same as building address</span>
                </label>
              )}
              <input
                id='address'
                type='text'
                placeholder='Full address'
                {...formik.getFieldProps('address')}
                ref={addressInputRef}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
                onChange={handleBillingFieldChange}
                className='inputField'
              />
              {formik.touched.address && formik.errors.address && (
                <div className={styles.error}>{formik.errors.address}</div>
              )}
            </div>
          </div>
          {isSaveManually && (
            <>
              <div className={`${styles.formGrid} ${styles.formGridStacked}`}>
                <div
                  className={`${styles.item} inputWrapper`}
                  title='Enter the unit number (if applicable)'
                >
                  <label htmlFor='unitNumber' className='label'>
                    Unit number
                  </label>
                  <input
                    id='unitNumber'
                    type='text'
                    placeholder='Unit number'
                    {...formik.getFieldProps('unitNumber')}
                    onChange={handleBillingFieldChange}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                    className='inputField'
                  />
                  {formik.touched.unitNumber && formik.errors.unitNumber && (
                    <div className={styles.error}>{formik.errors.unitNumber}</div>
                  )}
                </div>
                {/* Street Number */}
                <div
                  className={`${styles.item} inputWrapper`}
                  title='Enter the street number or building number'
                >
                  <label htmlFor='house' className='label'>
                    Street number
                  </label>
                  <input
                    id='house'
                    type='text'
                    placeholder='Street number '
                    {...formik.getFieldProps('house')}
                    onChange={handleBillingFieldChange}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                    className='inputField'
                  />
                  {formik.touched.house && formik.errors.house && (
                    <div className={styles.error}>{formik.errors.house}</div>
                  )}
                </div>
                {/* Street */}
                <div className={`${styles.item} inputWrapper`} title='Enter the street name'>
                  <label htmlFor='street' className='label'>
                    Street
                  </label>
                  <input
                    id='street'
                    type='text'
                    placeholder='Street name'
                    {...formik.getFieldProps('street')}
                    onChange={handleBillingFieldChange}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                    className='inputField'
                  />
                  {formik.touched.street && formik.errors.street && (
                    <div className={styles.error}>{formik.errors.street}</div>
                  )}
                </div>

                {/* City */}
                <div className={`${styles.item} inputWrapper`} title='Enter the city name'>
                  <label htmlFor='city' className='label'>
                    City <span className={styles.required}>*</span>
                  </label>
                  <input
                    id='city'
                    type='text'
                    placeholder='City'
                    {...formik.getFieldProps('city')}
                    onChange={handleBillingFieldChange}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                    className='inputField'
                  />
                  {formik.touched.city && formik.errors.city && (
                    <div className={styles.error}>{formik.errors.city}</div>
                  )}
                </div>

                {/* Province */}
                <div className={`${styles.item} inputWrapper`} title='Enter the province name'>
                  <label htmlFor='province' className='label'>
                    Province
                  </label>
                  <input
                    id='province'
                    type='text'
                    placeholder='Province'
                    {...formik.getFieldProps('province')}
                    onChange={handleBillingFieldChange}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                    className='inputField'
                  />
                  {formik.touched.province && formik.errors.province && (
                    <div className={styles.error}>{formik.errors.province}</div>
                  )}
                </div>

                {/* Postal Code */}
                <div className={`${styles.item} inputWrapper`} title='Enter the postal code'>
                  <label htmlFor='postal' className='label'>
                    Postal code <span className={styles.required}>*</span>
                  </label>
                  <input
                    id='postal'
                    type='text'
                    placeholder='Postal code'
                    {...formik.getFieldProps('postal')}
                    onChange={handleBillingFieldChange}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                    className='inputField'
                  />
                  {formik.touched.postal && formik.errors.postal && (
                    <div className={styles.error}>{formik.errors.postal}</div>
                  )}
                </div>

                {/* Province Code */}
                <div className={`${styles.item} inputWrapper`} title='Enter the province code'>
                  <label htmlFor='provinceCode' className='label'>
                    Province code <span className={styles.required}>*</span>
                  </label>
                  <input
                    id='provinceCode'
                    type='text'
                    placeholder='Province code'
                    {...formik.getFieldProps('provinceCode')}
                    onChange={handleBillingFieldChange}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                    className='inputField'
                  />
                  {formik.touched.provinceCode && formik.errors.provinceCode && (
                    <div className={styles.error}>{formik.errors.provinceCode}</div>
                  )}
                </div>

                {/* Country */}
                <div className={`${styles.item} inputWrapper`} title='Country is set to Canada'>
                  <label htmlFor='country' className='label'>
                    Country <span className={styles.required}>*</span>
                  </label>
                  <input
                    id='country'
                    type='text'
                    placeholder='Country'
                    {...formik.getFieldProps('country')}
                    disabled
                    className='inputField'
                  />
                  {formik.touched.country && formik.errors.country && (
                    <div className={styles.error}>{formik.errors.country}</div>
                  )}
                </div>
              </div>
            </>
          )}
          </div>
          <div className={styles.formFooter}>
            {submitError && (
              <div
                className={styles.error}
                role='alert'
                style={{
                  marginBottom: '12px',
                  padding: '12px',
                  borderRadius: '8px',
                  background: '#FEF2F2',
                }}
              >
                {submitError}
              </div>
            )}
            {loading && (
              <div className='flex flex-col items-center gap-3 py-4 mb-4'>
                <div className='w-5 h-5 border-2 border-[#E5E7EB] border-t-[#0055FF] rounded-full animate-spin' />
                <p className='text-[12px] text-[#6B7285] text-center'>
                  Your bank may ask you to verify this card (3DS). Complete any prompts that appear.
                </p>
              </div>
            )}
            <div className={styles.item100}>
              <button type='submit' className={'submitBtn'} disabled={loading}>
                {loading ? 'Verifying card…' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </motion.main>
    </>
  );
});

export default AddCard;
